import app from 'flarum/forum/app';

/** Wait this long after a keystroke before searching, in milliseconds. */
const DEBOUNCE = 250;

/** Most suggestions to show at once. */
const LIMIT = 5;

/**
 * Search state for @mention suggestions in a plain textarea.
 *
 * Owned by the component that renders the textarea rather than by the dropdown
 * itself: Mithril creates and discards component instances as it redraws, so
 * state kept inside one is not reliably the state being displayed.
 */
export default class MentionSearch {
  constructor(onchange, onchoose) {
    this.results = [];
    this.loading = false;
    this.index = 0;
    this.query = null;
    this.timeout = null;
    // Called whenever the visible state changed and a redraw is due.
    this.onchange = onchange || (() => {});
    // Called when the user picks a suggestion with the keyboard.
    this.onchoose = onchoose || (() => {});
  }

  /**
   * The @mention the caret currently sits in, if any.
   *
   * @returns {{start: number, typed: string}|null}
   */
  static mentionAt(value, cursor) {
    const text = value || '';
    const at = cursor ?? text.length;
    const before = text.slice(0, at);

    // Only the run since the last @, and only when that @ starts a word —
    // "ali@ornek.com" must not open a suggestion list.
    const match = before.match(/(?:^|[\s(\[])@([a-zA-Z0-9_-]*)$/);

    if (!match) return null;

    return { start: at - match[1].length - 1, typed: match[1] };
  }

  /**
   * True while a mention is actively being typed.
   *
   * Stays open across an empty result too: the strip replaces the tool bar, and
   * flipping between them on every keystroke made the composer jump.
   */
  get open() {
    return this.query !== null && this.query.length > 0;
  }

  /** React to the text or caret having changed. */
  refresh(value, cursor) {
    const mention = MentionSearch.mentionAt(value, cursor);

    if (!mention) {
      if (this.query !== null || this.results.length) {
        this.reset();
        this.onchange();
      }
      return;
    }

    if (mention.typed === this.query) return;

    this.query = mention.typed;
    this.index = 0;

    clearTimeout(this.timeout);

    // An empty query would list arbitrary users; wait for a character.
    if (mention.typed.length === 0) {
      this.results = [];
      this.loading = false;
      this.onchange();
      return;
    }

    this.loading = true;
    this.onchange();

    this.timeout = setTimeout(() => this.search(mention.typed), DEBOUNCE);
  }

  search(typed) {
    app.store
      .find('users', { filter: { q: typed }, page: { limit: LIMIT } })
      .then((users) => {
        // A later keystroke may have superseded this search.
        if (this.query !== typed) return;

        this.results = Array.isArray(users) ? users : [];
        this.loading = false;
        this.onchange();
      })
      .catch(() => {
        if (this.query !== typed) return;

        this.results = [];
        this.loading = false;
        this.onchange();
      });
  }

  /**
   * Handle a key pressed in the textarea.
   *
   * @returns {boolean} true when the key was consumed by the list.
   */
  onkeydown(e) {
    if (this.results.length === 0) return false;

    switch (e.key) {
      case 'ArrowDown':
        this.move(1);
        return true;
      case 'ArrowUp':
        this.move(-1);
        return true;
      case 'Enter':
      case 'Tab':
        this.onchoose(this.results[this.index]);
        return true;
      case 'Escape':
        this.reset();
        this.onchange();
        return true;
      default:
        return false;
    }
  }

  move(delta) {
    const count = this.results.length;

    this.index = (this.index + delta + count) % count;
    this.onchange();
  }

  reset() {
    clearTimeout(this.timeout);
    this.results = [];
    this.loading = false;
    this.query = null;
    this.index = 0;
  }
}
